import React, { FC, useContext } from 'react';
import { FeatureFlags, Filter } from '@fabric-ui/components';
import {
  AiMagic,
  Delete,
  Duplicate,
  Export,
  FitWidget,
  MaximizeFullScreen as Maximize,
  MinimizeFullScreen as Minimize,
  Script,
} from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Divider, Menu, MenuItem } from '@mui/material';
import { patchHistoricalQueryTimeRange } from '@views/CustomDashboards/hooks';
import { useAvContext } from '../../../../context/AvContextProvider';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { generateTempIdForWidget, isHistoricalQuery } from '../../CustomDashboardsUtils';
import { useExportWidget } from '../../hooks/useExportWidget';
import {
  AllTypeWidgets,
  BarTypeWidget,
  CustomDashboardDto,
  DisplayModeOptions,
  PivotTableTypeWidget,
  TableSubType,
  TextSubType,
  TileSubType,
  WidgetCategory,
} from '../../types';

const maximizeIcon = <Maximize />;
const minimizeIcon = <Minimize />;

interface WidgetToolbarActionsProps {
  anchorEl: Element;
  closeMenu: () => void;
  widget: AllTypeWidgets;
  globalFilters?: Filter;
  isEditMode?: boolean;
  handleExpand: () => void;
}

const WidgetToolbarActions: FC<WidgetToolbarActionsProps> = ({ anchorEl, widget, isEditMode, closeMenu, globalFilters, handleExpand }) => {
  const { setNewDashboard, expandedWidgetId } = useContext(CustomDashboardContext);
  const { featureFlags } = useAvContext();

  const isMaximized = expandedWidgetId === widget.id;
  const isScroll = (widget as BarTypeWidget).definition.custom?.displayMode === DisplayModeOptions.SCROLL;

  const { exportWidget } = useExportWidget();

  const deleteWidget = () => {
    closeMenu();
    setNewDashboard(prev => ({ ...prev, widgets: prev.widgets.filter(w => w.id !== widget.id) }));
  };

  const duplicateWidget = () => {
    closeMenu();
    setNewDashboard(
      prev =>
        ({
          ...prev,
          widgets: [
            ...prev.widgets,
            {
              ...widget,
              id: generateTempIdForWidget(),
              definition: {
                ...widget.definition,
                coords: {
                  ...widget.definition.coords,
                  y: widget.definition.coords.y + 1,
                },
              },
            },
          ],
        }) as CustomDashboardDto
    );
  };

  const setDisplayMode = () => {
    setNewDashboard(prev => ({
      ...prev,
      widgets: prev.widgets.map(w => {
        if (w.id === widget.id) {
          return {
            ...(w as BarTypeWidget),
            definition: {
              ...w.definition,
              custom: {
                ...(w as BarTypeWidget).definition.custom,
                displayMode: isScroll ? DisplayModeOptions.FIT : DisplayModeOptions.SCROLL,
              },
            },
          };
        }
        return w;
      }),
    }));
  };

  const handleToggleBinnedTimeBuckets = () => {
    setNewDashboard(prev => ({
      ...prev,
      widgets: prev.widgets.map(w => (w.id === widget.id ? { ...w, binnedTimeBuckets: !w.binnedTimeBuckets } : w)),
    }));
  };

  const onExportWidget = async () => {
    const [request] = widget.requests;

    const query = {
      ...request,
      timeRange: patchHistoricalQueryTimeRange(request.timeRange),
      top: request.top ? { ...request.top, size: 40000, offset: 0 } : undefined,
    };

    exportWidget({
      widget: { ...widget, requests: [query] } as Exclude<AllTypeWidgets, PivotTableTypeWidget>,
      globalFilters,
    });
  };

  return (
    <Menu
      className="draggableCancel"
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        pointerEvents: 'none',
        '.MuiMenu-paper': {
          pointerEvents: 'auto',
          minWidth: 200,
        },
        '.MuiMenuItem-root': {
          paddingLeft: 1,
          paddingRight: 1,
          minHeight: 36,
          svg: {
            width: 18,
          },
        },
      }}
      open={Boolean(anchorEl)}>
      {widget.category !== WidgetCategory.Text && (
        <MenuItem onClick={handleExpand}>
          <Box sx={{ ...flex.row, gap: 1 }}>
            {isMaximized ? minimizeIcon : maximizeIcon}
            {isMaximized ? 'Minimize' : 'Full Screen'}
          </Box>
        </MenuItem>
      )}
      {isEditMode && (
        <MenuItem onClick={duplicateWidget}>
          <Box sx={{ ...flex.row, gap: 1 }}>
            <Duplicate />
            Duplicate
          </Box>
        </MenuItem>
      )}
      {isEditMode && widget.category === WidgetCategory.Bar && (
        <MenuItem onClick={setDisplayMode}>
          <Box sx={{ ...flex.row, gap: 1 }}>
            {isScroll ? <FitWidget /> : <Script />}
            {isScroll ? 'Fit to Widget Size' : 'Enable Scrolling '}
          </Box>
        </MenuItem>
      )}

      {![TextSubType.Text, TileSubType.Tile, TableSubType.PivotTable].includes(widget.type as any) &&
        !widget.drillDownHierarchy?.activeIndex && (
          <MenuItem onClick={onExportWidget}>
            <Box sx={{ ...flex.row, gap: 1 }}>
              <Export />
              Export As CSV
            </Box>
          </MenuItem>
        )}

      {featureFlags[FeatureFlags.QueryAdmin] && isEditMode && widget.requests[0] && isHistoricalQuery(widget.requests[0]) && (
        <MenuItem onClick={handleToggleBinnedTimeBuckets}>
          <Box sx={{ ...flex.row, gap: 1 }}>
            <AiMagic />
            {widget.binnedTimeBuckets ? 'Disable Bin Opt' : 'Enable Bin Opt'}
          </Box>
        </MenuItem>
      )}

      {isEditMode && (
        <>
          <Divider />
          <MenuItem onClick={deleteWidget}>
            <Box sx={{ ...flex.row, gap: 1 }}>
              <Delete />
              Delete
            </Box>
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export default WidgetToolbarActions;
